import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";
import useMediaQuery from "../shared/useMediaQuery";

function Dialog(props) {
  return <DialogPrimitive.Root {...props} />;
}

function DialogTrigger(props) {
  return <DialogPrimitive.Trigger {...props} />;
}

function DialogPortal(props) {
  return <DialogPrimitive.Portal {...props} />;
}

function DialogClose(props) {
  return <DialogPrimitive.Close {...props} />;
}

const DialogOverlay = React.forwardRef(({ className, style, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "tw-fixed tw-inset-0 tw-z-50 tw-bg-black/70",
      "data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0",
      "data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0",
      className
    )}
    style={{ position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.7)", ...style }}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

const DialogContent = React.forwardRef(
  ({ className, style, children, showCloseButton = true, ...props }, ref) => {
    const isMobile = useMediaQuery("(max-width: 640px)");

    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            "tw-fixed tw-left-1/2 tw-top-1/2 tw-z-50 tw-w-[90vw] tw-max-w-lg -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-xl tw-border tw-border-white/[0.08] tw-bg-surface-secondary tw-p-5 tw-shadow-2xl",
            "data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0 data-[state=open]:tw-zoom-in-95",
            "data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=closed]:tw-zoom-out-95",
            "member-scroll",
            className
          )}
          style={{
            position: "fixed",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 50,
            width: isMobile ? "calc(100vw - 16px)" : "90vw",
            maxWidth: 560,
            maxHeight: isMobile ? "78vh" : "88vh",
            overflowY: "auto",
            overflowX: "hidden",
            borderRadius: isMobile ? 12 : 14,
            border: "1px solid rgba(255,255,255,0.08)",
            background: "#1e1e1e",
            color: "#fff",
            padding: isMobile ? "16px 14px" : 24,
            boxSizing: "border-box",
            boxShadow: "0 30px 60px -20px rgba(0,0,0,0.7)",
            ...style,
          }}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              style={{
                position: "absolute",
                top: isMobile ? 10 : 14,
                right: isMobile ? 10 : 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 8,
                background: "rgba(255,255,255,0.06)",
                border: "none",
                color: "#c3c8cc",
                cursor: "pointer",
              }}
            >
              <X size={14} />
              <span
                className="tw-sr-only"
                style={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  padding: 0,
                  margin: -1,
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }}
              >
                Close
              </span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = "DialogContent";

function DialogHeader({ className, style, ...props }) {
  return (
    <div
      className={cn("tw-mb-3", className)}
      style={{ marginBottom: 16, paddingRight: 28, ...style }}
      {...props}
    />
  );
}

const DialogTitle = React.forwardRef(({ className, style, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("tw-text-base tw-font-bold tw-text-white", className)}
    style={{ fontSize: 16.5, fontWeight: 700, color: "#fff", margin: 0, ...style }}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef(({ className, style, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("tw-text-sm tw-text-muted", className)}
    style={{ fontSize: 13.5, color: "#c3c8cc", marginTop: 5, ...style }}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

function DialogFooter({ className, style, ...props }) {
  const isMobile = useMediaQuery("(max-width: 640px)");

  return (
    <div
      className={cn("tw-mt-4 tw-flex tw-justify-end", className)}
      style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: isMobile ? 8 : 0, ...style }}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
};
